import { useState, useEffect, useCallback, useRef } from 'react'
import { User, Session } from '@supabase/supabase-js'
import { supabase } from '@/integrations/supabase/client'
import { useToast } from '@/hooks/use-toast'
import { sessionManager } from '@/utils/sessionManager'
import { auditLogger } from '@/utils/auditLogger'

export const useAuth = () => {
  const [user, setUser] = useState<User | null>(null)
  const [session, setSession] = useState<Session | null>(null)
  const [loading, setLoading] = useState(true)
  const [userRole, setUserRole] = useState<'superadmin' | 'admin' | 'customer' | null>(null)
  const [tenantId, setTenantId] = useState<string | null>(null)
  const { toast } = useToast()
  
  // Prevent multiple simultaneous role fetches
  const fetchingRoleRef = useRef(false)
  const lastUserIdRef = useRef<string | null>(null)

  const RESET_GUARD_KEY = 'hrz_reset_guard'
  const RESET_GUARD_TTL_MS = 15 * 60 * 1000
  const isResetGuardActive = (checkEmail?: string): boolean => {
    try {
      const raw = localStorage.getItem(RESET_GUARD_KEY)
      if (!raw) return false
      const parsed = JSON.parse(raw)
      const isActive = typeof parsed?.ts === 'number' && Date.now() - parsed.ts < RESET_GUARD_TTL_MS
      
      // If checking for specific email, only return true if email matches
      if (checkEmail && parsed?.email) {
        return isActive && parsed.email.toLowerCase() === checkEmail.toLowerCase()
      }
      
      return isActive
    } catch {
      return false
    }
  }
  const clearResetGuard = () => {
    try { localStorage.removeItem(RESET_GUARD_KEY) } catch {}
  }

  useEffect(() => {
    // Set up session warning and expiry handlers
    sessionManager.onWarning(() => {
      toast({
        title: "Sitzung läuft bald ab",
        description: "Ihre Sitzung läuft in 5 Minuten ab.",
      })
    })

    sessionManager.onExpired(() => {
      toast({
        variant: "destructive",
        title: "Sitzung abgelaufen",
        description: "Sie wurden aus Sicherheitsgründen abgemeldet.",
      })
      // Redirect will be handled by sessionManager
    })

    // Set up auth state listener FIRST
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        // SECURITY: Ignore password recovery and user update events to prevent auto-login
        if (event === 'PASSWORD_RECOVERY' || event === 'USER_UPDATED') {
          console.log('🔐 Ignoring password recovery/update event to prevent auto-login:', event)
          return
        }
        
        console.log('🔑 Auth state change:', event, 'Session:', !!session)

        // Block only SIGNED_IN events during password recovery (not TOKEN_REFRESHED)
        // This prevents auto-login in other tabs when reset is requested
        // BUT: Only block if the email matches the one that requested the reset
        const sessionEmail = session?.user?.email
        if (event === 'SIGNED_IN' && sessionEmail && isResetGuardActive(sessionEmail) && window.location.pathname !== '/reset-password') {
          console.warn('🔒 Blocking cross-tab SIGNED_IN during password recovery for', sessionEmail)
          setSession(null)
          setUser(null)
          // Local sign out to clear this tab session without affecting the reset tab
          supabase.auth.signOut({ scope: 'local' }).catch(() => {})
          return
        }
        
        setSession(session)
        setUser(session?.user ?? null)
        
        if (session?.user) {
          // Start session management for authenticated users
          sessionManager.startTimer()
          
          // Defer role fetching to prevent deadlocks
          setTimeout(() => {
            fetchUserRole(session.user.id)
          }, 0)
        } else {
          // Stop session management when user logs out
          sessionManager.stopTimer()
          setUserRole(null)
          setTenantId(null)
        }
        setLoading(false)
      }
    )

    // THEN check for existing session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session)
      setUser(session?.user ?? null)
      if (session?.user) {
        sessionManager.startTimer()
        setTimeout(() => {
          fetchUserRole(session.user.id)
        }, 0)
      }
      setLoading(false)
    })

    return () => {
      subscription.unsubscribe()
      sessionManager.stopTimer()
    }
  }, [])

  const fetchUserRole = useCallback(async (userId: string) => {
    // Prevent multiple simultaneous fetches for the same user
    if (fetchingRoleRef.current && lastUserIdRef.current === userId) {
      return
    }

    fetchingRoleRef.current = true
    lastUserIdRef.current = userId

    try {
      // Check user role from database
      const { data: userRole, error: roleError } = await supabase
        .from('user_roles')
        .select('role')
        .eq('user_id', userId)
        .maybeSingle()

      if (roleError) {
        console.error('❌ Error fetching user role:', roleError)
        setUserRole('customer')
        return
      }

      // If role exists in user_roles table, use it
      if (userRole) {
        setUserRole(userRole.role)
        
        // If user is admin, get or create their tenant ID
        if (userRole.role === 'admin') {
          try {
            // First check if admin already has a tenant
            const { data: existingTenant, error: tenantError } = await supabase
              .from('tenants')
              .select('id, name, status')
              .eq('admin_user_id', userId)
              .maybeSingle()
            
            if (existingTenant && !tenantError) {
              setTenantId(existingTenant.id)
            } else {
              // If no tenant exists, create one using the function
              console.log('🏗️ Attempting to initialize admin demo data for user:', userId)
              const { data: tenantId, error: createError } = await supabase
                .rpc('initialize_admin_demo_data', { admin_user_uuid: userId })
              
              if (createError) {
                console.error('❌ Error initializing admin tenant:', createError)
                console.log('ℹ️ Admin can still access data via RLS policies')
                setTenantId(null)
              } else {
                console.log('✅ Admin tenant initialized:', tenantId)
                setTenantId(tenantId)
              }
            }
          } catch (error) {
            console.error('❌ Error setting up admin tenant:', error)
            setTenantId(null)
          }
        } else {
          setTenantId(null)
        }
      } else {
        // Fallback: if no role in user_roles, check if user is admin via tenants table
        console.log('ℹ️ No role found in user_roles, checking tenants table')
        const { data: tenantAdmin, error: tenantError } = await supabase
          .from('tenants')
          .select('id')
          .eq('admin_user_id', userId)
          .maybeSingle()
        
        if (tenantAdmin && !tenantError) {
          console.log('✅ User is admin via tenants table')
          setUserRole('admin')
          setTenantId(tenantAdmin.id)
        } else {
          // Default to customer
          console.log('ℹ️ Defaulting to customer role')
          setUserRole('customer')
          setTenantId(null)
        }
      }
    } catch (error) {
      console.error('❌ Error fetching user role:', error)
      setUserRole('customer')
      setTenantId(null)
    } finally {
      fetchingRoleRef.current = false
    }
  }, [])

  const signIn = async (email: string, password: string) => {
    try {
      const { error } = await supabase.auth.signInWithPassword({
        email,
        password,
      })
      
      if (error) throw error
      
      // Log successful login
      await auditLogger.logLogin('password')
      
      toast({
        title: "Erfolgreich angemeldet",
        description: "Sie werden weitergeleitet...",
      })
      
      // Let AuthRedirect component handle the redirect
    } catch (error: any) {
      // Log failed login attempt
      await auditLogger.logSecurityEvent('invalid_access', { 
        email, 
        error: error.message,
        attempt_type: 'login'
      })
      
      toast({
        variant: "destructive",
        title: "Anmeldung fehlgeschlagen",
        description: error.message,
      })
      throw error
    }
  }

  const signUp = async (email: string, password: string, metadata?: any) => {
    try {
      setLoading(true)
      
      const redirectUrl = `${window.location.origin}/`
      
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          emailRedirectTo: redirectUrl,
          data: metadata
        }
      })

      if (error) {
        if (error.message.includes('User already registered')) {
          toast({
            variant: "destructive",
            title: "Registrierung fehlgeschlagen",
            description: "Ein Konto mit dieser E-Mail-Adresse existiert bereits. Versuchen Sie sich anzumelden."
          })
        } else if (error.message.includes('Invalid email')) {
          toast({
            variant: "destructive", 
            title: "Ungültige E-Mail",
            description: "Bitte geben Sie eine gültige E-Mail-Adresse ein."
          })
        } else {
          toast({
            variant: "destructive",
            title: "Registrierung fehlgeschlagen", 
            description: error.message
          })
        }
        throw error
      }

      // Send welcome email after successful registration
      if (data.user && !error) {
        try {
          const { error: welcomeError } = await supabase.functions.invoke('send-welcome-email', {
            body: {
              userId: data.user.id,
              email: data.user.email,
              tenantId: metadata?.tenant_id
            }
          })
          
          if (welcomeError) {
            console.error('Welcome email error:', welcomeError)
          }
        } catch (welcomeErr) {
          console.error('Failed to send welcome email:', welcomeErr)
          // Don't throw error here as registration was successful
        }
      }

      toast({
        title: "Registrierung erfolgreich!",
        description: "Bitte bestätigen Sie Ihre E-Mail-Adresse, um Ihr Konto zu aktivieren."
      })

      return { user: data.user, error: null }
    } catch (error: any) {
      console.error('SignUp error:', error)
      throw error
    } finally {
      setLoading(false)
    }
  }

  const signOut = async () => {
    try {
      console.log('🚪 Signing out user...')
      
      // Log logout before actually signing out
      await auditLogger.logLogout()
      
      // Stop session management
      sessionManager.stopTimer()
      
      // Clear local state immediately
      setUser(null)
      setSession(null)
      setUserRole(null)
      setTenantId(null)
      
      // Clear any auth-related localStorage items
      Object.keys(localStorage).forEach(key => {
        if (key.startsWith('supabase.auth.') || key.includes('sb-')) {
          localStorage.removeItem(key)
        }
      })
      
      // Sign out from Supabase
      const { error } = await supabase.auth.signOut({ scope: 'global' })
      
      if (error) {
        console.error('❌ Error during sign out:', error)
      }
      
      console.log('✅ Successfully signed out')
      
      toast({
        title: "Abgemeldet",
        description: "Auf Wiedersehen!",
      })
      
      // Force page reload to ensure clean state
      setTimeout(() => {
        window.location.href = '/'
      }, 1000)
      
    } catch (error: any) {
      console.error('❌ Error signing out:', error)
      toast({
        variant: "destructive",
        title: "Abmeldung fehlgeschlagen",
        description: error.message,
      })
      // Force clear and redirect even on error
      setTimeout(() => {
        window.location.href = '/'
      }, 1000)
    }
  }

  const sendMagicLink = async (email: string) => {
    try {
      const redirectUrl = `${window.location.origin}/`
      
      const { error } = await supabase.auth.signInWithOtp({ 
        email,
        options: {
          emailRedirectTo: redirectUrl
        }
      })
      
      if (error) throw error
      
      // Log magic link request
      await auditLogger.logLogin('magic_link')
      
      toast({
        title: "Magic Link versendet",
        description: "Überprüfen Sie Ihre E-Mail für den Anmeldelink.",
      })
    } catch (error: any) {
      // Log failed magic link attempt
      await auditLogger.logSecurityEvent('invalid_access', { 
        email, 
        error: error.message,
        attempt_type: 'magic_link'
      })
      
      toast({
        variant: "destructive",
        title: "Fehler",
        description: error.message || "Magic Link konnte nicht gesendet werden.",
      })
    }
  }

  return {
    user,
    session,
    userRole,
    tenantId,
    loading,
    signIn,
    signUp,
    signOut,
    sendMagicLink,
  }
}